<template>
  <div class="card space-y-6 pb-3">
    <div
      class="flex items-center justify-center space-x-3 rounded-t-md py-2"
      :class="{
        'bg-green-600': passStatic,
        'bg-amber-500': !passStatic,
      }"
    >
      <CheckCircleIcon v-if="passStatic" class="h-7 w-7 fill-white" />
      <ExclamationTriangleIcon v-else class="h-7 w-7 stroke-white" />
      <h1 class="text-lg text-white">{{ headingText }}</h1>
    </div>

    <div class="px-4">
      <SiteEntryUserInformation />

      <ul class="my-4" data-test="checks-list">
        <li
          v-for="siteEntryCheck in siteEntryChecks"
          :key="siteEntryCheck.title"
          class="mb-7"
        >
          <div class="flex items-center space-x-3">
            <ExclamationTriangleIcon
              v-if="!siteEntryCheck.passed && siteEntryCheck.canContinue"
              class="w-6 stroke-yellow-600"
            />
            <CheckCircleOutlineIcon
              v-else-if="siteEntryCheck.canContinue"
              class="w-6 stroke-green-600"
            />
            <p data-test="check-title">
              {{ siteEntryCheck.title }}
            </p>
          </div>

          <ul
            v-if="siteEntryCheck.checks"
            class="mt-0.5"
            data-test="check-details"
          >
            <li
              v-for="check in siteEntryCheck.checks"
              :key="
                check.name !== ''
                  ? check.name
                  : `${siteEntryCheck.title}-feedback`
              "
              class="mb-0.5 ml-9 text-sm"
            >
              <p class="font-bold">
                {{ check['displayName'] ?? check.name }}
              </p>
              <p>
                {{ check['feedback'] ?? '' }}
              </p>
            </li>
          </ul>
        </li>
      </ul>
      <hr v-if="!passStatic" class="my-4 text-gray-300" />
      <div v-if="!passStatic" class="flex space-x-3">
        <div class="w-6">
          <CheckCircleOutlineIcon class="w-6 stroke-green-600" />
        </div>
        <div class="text-sm">
          <p class="font-bold">Access granted today</p>
          <p>
            You may enter the site this time. Please update your required
            credentials before your next visit. The client may deny future
            access if this is not resolved.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import SiteEntryUserInformation from '@/components/SiteEntryUserInformation.vue'
  import { computed, PropType } from 'vue'
  import {
    CheckType,
    SiteEntryCheckType,
  } from '@/storage/siteEntryConfirmation'
  import { AccreditationCheckType } from '@/services/api/accreditation'
  import {
    ExclamationTriangleIcon,
    CheckCircleIcon as CheckCircleOutlineIcon,
  } from '@heroicons/vue/24/outline'
  import { CheckCircleIcon } from '@heroicons/vue/24/solid'

  const props = defineProps({
    passStatic: {
      required: true,
      type: Boolean,
    },
    siteEntryChecks: {
      required: true,
      type: Array as PropType<
        SiteEntryCheckType<CheckType | AccreditationCheckType>[]
      >,
    },
  })

  const headingText = computed(() =>
    props.passStatic ? 'Access Granted' : 'Access Warning',
  )
</script>
